'use client'

import React, { useEffect, useState } from 'react'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useRouter } from 'next/router'
import CommonApi from '../api'
import CustomModal from '@/src/components/Modal'
import Button from '@/src/components/button'
import { CircularProgress, Button as MuiButton, Tooltip, Typography } from '@mui/material'

interface DnsRecord {
  name: string
  type: string
  value: string
}

interface DomainRecord {
  name: string
  type: string
  value: string
}

const ViewDns = () => {
  const router = useRouter()
  const businessId = router.query?.code
  const type = router.query?.type
  const [loading, setLoading] = useState(true)
  const [dnsList, setDnsList] = useState<DnsRecord[] | null>(null)
  const [domainList, setDomainList] = useState<DomainRecord[] | null>([])
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [modalOpen, setModalOpen] = useState<boolean>(false)

  const getDns = async (
    name: string,
    customer_email: string,
    reply_email: string,
    code: string,
    is_siganture_added: boolean
  ) => {
    try {
      const resp = await CommonApi({
        path: `postmark_signatures`,
        method: 'post',
        data: {
          reply_email: reply_email,
          customer_email: customer_email,
          business_name: name,
          code: code,
          is_siganture_added: is_siganture_added
        }
      })
      if (resp?.status) {
        setDnsList(resp.data)
      }
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  const getbusinessData = async (code: any) => {
    try {
      const resp = await CommonApi({
        method: 'get',
        path: `get_business_details${code ? `?code=${atob(code)}` : ''}`
      })
      if (resp.status) {
        getDns(
          resp.data?.business_name,
          resp.data?.customer_email,
          resp.data?.reply_email,
          resp.data?.code,
          resp.data?.is_siganture_added
        )
      }
    } catch (err) {}
  }

  const getDomain = async (code: any) => {
    try {
      const resp = await CommonApi({
        method: 'patch',
        path: `custom-domain/settings${code ? `?code=${atob(code)}` : ''}`
      })
      if (resp.status) {
        setDomainList(resp?.data)
      } else {
        setErrorMsg(resp?.msg)
      }
    } catch (err) {
    } finally {
      setLoading(false)
    }
  }

  const handleDataFetch = () => {
    if (businessId) {
      setLoading(true)
      if (type == 'domain') {
        getDomain(businessId)
      } else {
        getbusinessData(businessId)
      }
    }
  }

  useEffect(() => {
    if (businessId) {
      setModalOpen(true)
    }
  }, [businessId])

  const handleConfirmYes = () => {
    setModalOpen(false)
    handleDataFetch()
  }

  const handleConfirmCancel = () => {
    setModalOpen(false)
    window.close()
  }

  const handleCopyFull = (item: DomainRecord, index: number) => {
    navigator.clipboard.writeText(JSON.stringify(item, null, 2))
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-white p-4">
      {loading ? (
        <>
          <CircularProgress />
          <p className="mt-4 text-lg">
            {type == 'domain'
              ? 'Please wait while generating the server Domain...'
              : 'Please wait while generating the server DNS...'}
          </p>
        </>
      ) : type == 'domain' ? (
        domainList && domainList.length > 0 ? (
          <div className="w-full max-w-3xl space-y-6">
            {domainList.map((item, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 shadow-md rounded-xl p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Name:</p>
                <p className="text-md font-medium mb-4 break-words">{item?.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Type:</p>
                <p className="text-md font-medium mb-4 break-words">{item?.type}</p>

                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Value:</p>
                <p className="text-sm mb-2 break-all">{item?.value}</p>

                <Tooltip
                  title={
                    copiedIndex === index
                      ? 'Copied!'
                      : type == 'domain'
                        ? 'Copy Domain'
                        : 'Copy DNS'
                  }
                  placement="top"
                >
                  <MuiButton
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopyFull(item, index)}
                  >
                    Copy
                  </MuiButton>
                </Tooltip>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-xl p-6 w-full max-w-md text-center">
              <p className="text-lg font-medium">
                DNS has already been generated and cannot be created again.
              </p>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                You can only generate them once.
              </p>
            </div>
          </>
        )
      ) : dnsList && dnsList.length > 0 ? (
        <div className="w-full max-w-3xl space-y-6">
          {dnsList.map((record, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 shadow-md rounded-xl p-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Name:</p>
              <p className="text-md font-medium mb-4 break-words">{record.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Type:</p>
              <p className="text-md font-medium mb-4 break-words">{record?.type}</p>

              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Value:</p>
              <p className="text-sm mb-2 break-all">{record?.value}</p>

              <Tooltip title={copiedIndex === index ? 'Copied!' : 'Copy DNS'} placement="top">
                <MuiButton
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => handleCopyFull(record, index)}
                >
                  Copy
                </MuiButton>
              </Tooltip>
            </div>
          ))}
          <p className="text-sm text-center text-gray-600 dark:text-gray-400 mt-6">
            You can only generate them once.
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-xl p-6 w-full max-w-md text-center">
          <p className="text-lg font-medium">
            DNS has already been generated and cannot be created again.
          </p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            You can only generate them once.
          </p>
        </div>
      )}
      <CustomModal
        classes="w-[37.5rem]  h-fit relative p-4  flex justify-between flex-col gap-8"
        handleClose={() => {
          handleConfirmCancel()
        }}
        open={modalOpen}
      >
        <div>
          <Typography variant="body1">
            Are you sure you want to proceed with {type === 'domain' ? 'domain' : 'DNS'} generation?
          </Typography>
        </div>
        <div className="flex gap-4 w-full justify-between">
          <Button text="Cancel" variant="text" onClick={handleConfirmCancel} />
          <Button variant="text" onClick={handleConfirmYes} text="Yes" />
        </div>
      </CustomModal>
    </div>
  )
}

export default ViewDns
